import { PromotionType } from '../../graphQL/commons'
import { IPromotionUiCart, IPromotionUiCartInput } from '../../interfaces'
import { getActivePromotionsFields } from '../common/getPromotionFields'
import formatPromotionMessage, {
  IFormatPromotionMessage,
} from '../formatPromotionMessage'
import formatPercentage from '../formatPercentage'
import flatTexts from '../flatTexts'

export const getComboPromotionUiDetails = ({
  activePromotion,
  cartProduct,
  config,
  cartProducts,
}: IPromotionUiCartInput): IPromotionUiCart => {
  const texts = config?.texts?.cartPromotions
  const { discounts, promotionalQty, price, quantity } = cartProduct
  const {
    fields: {
      customerBuysQuantity,
      customerGetsPercentage,
      limitQuantity,
      customerBuysItems,
      customerGetsItems,
      customerGetsQuantity,
      type,
    },
    promotion,
    count,
  } = getActivePromotionsFields(activePromotion)
  // Opt-in: a TOTAL promotion line whose every unit is gifted renders at its real
  // per-unit price instead of the value derived from `discountsNoCombo`, which
  // nets to the full price when everything is free.
  // Restricted to `quantity === promotionalQty` so a line that also carries paid
  // units of the same SKU keeps the discount-based calculation.
  // Off by default to preserve existing carts.
  const isFullyGiftedTotal =
    !!config?.options?.showTotalFreegoodZeroPrice &&
    type === PromotionType.TOTAL &&
    quantity === promotionalQty &&
    quantity > 0
  const [productToBuy] = customerBuysItems || []
  const [productToGet] = customerGetsItems || []
  const promotionPercentageFormat = config?.options?.promotionPercentageFormat
  const isSameProduct =
    customerBuysItems?.length === 1 &&
    customerGetsItems?.length === 1 &&
    productToBuy === productToGet
  const discountIsFullProduct: boolean =
    isSameProduct || !customerGetsPercentage
  const discount = discountIsFullProduct
    ? String(customerGetsQuantity + customerBuysQuantity)
    : formatPercentage(customerGetsPercentage, promotionPercentageFormat)
  const {
    freeItemsLabel = '',
    comboDifferentProductLabel = '',
    comboDifferentProductPercentageLabel = '',
    defaultSaving = '',
    defaultLimitLabel = '',
    comboPromotionOrigin = '',
    comboPromotionOriginORSymbol = '',
  } = flatTexts(texts)

  const formatArgs: IFormatPromotionMessage = {
    message: '',
    promotion,
    config,
    saving: discounts,
    amount: discounts,
    placeholders: {
      discount,
      value: String(customerBuysQuantity),
      count: String(promotionalQty),
    },
  }
  let label: string
  const promoOrigin: string[] = []
  if (isSameProduct) {
    label = formatPromotionMessage({
      ...formatArgs,
      message: freeItemsLabel as string,
    })
  } else {
    let newPromotionalQty = promotionalQty
    if (
      (type === PromotionType.COMBO || type === PromotionType.COMPLEX) &&
      config?.options?.freegoodAsCartGroup &&
      cartProducts
    ) {
      newPromotionalQty = cartProducts.reduce(
        (acc, product) =>
          product.sku === cartProduct.sku ? product.promotionalQty + acc : acc,
        0,
      )
    }
    const defaultLabel = discountIsFullProduct
      ? (comboDifferentProductLabel as string)
      : (comboDifferentProductPercentageLabel as string)
    label = formatPromotionMessage({
      ...formatArgs,
      placeholders: {
        ...formatArgs.placeholders,
        count: String(discountIsFullProduct ? newPromotionalQty : count),
      },
      message: defaultLabel,
    })
  }
  // promo origin for combo complex
  if (
    type === PromotionType.COMPLEX &&
    cartProducts &&
    config?.options?.showFreegoodsComboOrigin &&
    customerBuysItems.length > 1
  ) {
    promoOrigin.push(
      formatPromotionMessage({
        ...formatArgs,
        cartProducts,
        message: comboPromotionOrigin as string,
      }),
    )
    return {
      label,
      promoOrigin,
    }
  }
  // promo origin for combo
  if (
    type === PromotionType.COMBO &&
    config?.options?.showFreegoodsComboOrigin &&
    cartProducts
  ) {
    promoOrigin.push(
      formatPromotionMessage({
        ...formatArgs,
        cartProducts,
        message: comboPromotionOrigin as string,
        promoOriginSymbol: comboPromotionOriginORSymbol,
      }),
    )
    return {
      label,
      promoOrigin,
    }
  }

  if (discounts) {
    const discountsNoCombo = discounts - promotionalQty * price
    const finalDiscount =
      quantity === promotionalQty
        ? discountsNoCombo
        : discountsNoCombo / (quantity - promotionalQty)
    // Derived from `discounts`, not the promotion's `pricePerItem`: backends
    // disagree on that field's unit. For the same fully-gifted shape one returns
    // 0 (a per-unit price) and another the whole line's value, so reading it
    // would price a gift at several times its real cost.
    const value = isFullyGiftedTotal
      ? price - discounts / quantity
      : price - finalDiscount

    const result: IPromotionUiCart = {
      label,
      limit: limitQuantity
        ? formatPromotionMessage({
            ...formatArgs,
            limitQuantity,
            message: defaultLimitLabel as string,
          })
        : '',
      saving: formatPromotionMessage({
        ...formatArgs,
        message: defaultSaving as string,
      }),
      type,
      value,
      // Only present when it means something: tells calculateItemPriceTotal that
      // `value` is the net per-unit price, so `discounts` must not be subtracted.
      ...(isFullyGiftedTotal ? { isNetValue: true } : {}),
    }
    return result
  }
  return {
    label,
    type,
  }
}
